import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Target, UserCog, FileSignature } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import useReveal from "../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

const SERVICES = [
  {
    key: "ai-agents",
    icon: Bot,
    title: "AI Agents & Automations",
    desc: "Always-on AI agents and workflow automation that eliminate busywork and accelerate lead response.",
    href: "/services/ai-agents",
  },
  {
    key: "lead-generation",
    icon: Target,
    title: "Lead Generation",
    desc: "Done-for-you outbound pipeline: targeting, multichannel outreach, and qualified meetings booked.",
    href: "/services/lead-generation",
  },
  {
    key: "executive-assistants",
    icon: UserCog,
    title: "Executive Assistants",
    desc: "A trained executive assistant working inside your business — calendar, inbox, follow-up and coordination handled.",
    href: "/services/executive-assistants",
  },
  {
    key: "rfp-vendor-registration",
    icon: FileSignature,
    title: "RFP & Vendor Registration",
    desc: "Get registered, stay compliant, and win more public and enterprise contracts.",
    href: "/services/rfp-vendor-registration",
  },
];

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
    <span className="eyebrow text-[#16A06A]">{children}</span>
  </span>
);

const Hero = () => (
  <section data-testid="services-hero" className="relative pt-32 lg:pt-40 pb-16 lg:pb-20 overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[460px] w-[460px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>
    <div className="mx-auto max-w-[1100px] px-6 lg:px-8 text-center reveal">
      <Eyebrow>Other Services</Eyebrow>
      <h1 className="h1-hero mt-5 text-[#0A1628]">
        More Ways We Help You <span className="text-[#16A06A]">Grow.</span>
      </h1>
      <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-3xl mx-auto">
        Alongside our core operations service, RevGen offers a focused set of
        engagements to accelerate revenue, reclaim your time, and win more
        contracts — delivered with the same trained talent and operational
        discipline.
      </p>
      <div className="mt-8 flex justify-center">
        <a
          href={CALENDLY}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="services-hero-cta"
          className="group inline-flex items-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all"
        >
          Book a Strategy Call
          <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
        </a>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section data-testid="services-grid" className="py-20 lg:py-24">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Link
              key={s.key}
              to={s.href}
              data-testid={`services-card-${s.key}`}
              className="reveal group relative flex flex-col bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={20} />
              </div>
              <h3 className="mt-6 text-[18px] font-semibold text-[#0A1628] leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500 flex-1">
                {s.desc}
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#16A06A] group-hover:text-[#0E7C53]">
                Explore
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section data-testid="services-final-cta" className="relative bg-[#0A1628] overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Not sure where to start?</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Not sure which service fits? Let's map it together.
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a 30-minute call and we'll point you to the right RevGen
          solution.
        </p>
        <div className="mt-9 flex justify-center">
          <a
            href={CALENDLY}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-final-cta-btn"
            className="group inline-flex items-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-7 py-3.5 text-[15px] font-semibold shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all"
          >
            Book a Strategy Call
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

export default function Services() {
  useReveal();

  useEffect(() => {
    document.title = "Other Services | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "RevGen also offers AI agents & automations, lead generation, executive assistants, and RFP & vendor registration strategy. Book a call."
      );
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div data-testid="services-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <ServicesGrid />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
